/**
 * CBH — Prefetch on intent (safe + conservative)
 * Prefetches same-origin HTML documents when users hover/focus/touch a link.
 * Avoids checkout/cart/account and respects Save-Data / slow networks.
 */
(() => {
  'use strict';

  const origin = location.origin;
  const root = document.documentElement;

  const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  if (conn) {
    if (conn.saveData) return;
    const et = String(conn.effectiveType || '').toLowerCase();
    if (et.includes('2g')) return;
  }

  // Optional kill-switch: <html data-cbh-prefetch="0">
  if (root.dataset.cbhPrefetch === '0') return;

  const seen = new Set();
  let hoverTimer = 0;

  const SKIP_PATH_PREFIXES = [
    '/cart',
    '/checkout',
    '/account',
    '/apps',
    '/admin'
  ];

  const SKIP_EXT_RE = /\.(pdf|png|jpe?g|webp|gif|svg|zip|mp4|mov|webm|mp3|woff2?|ttf|otf)$/i;

  const STRIP_PARAMS = [
    'utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content',
    'gclid', 'fbclid', 'msclkid'
  ];

  function normalise(url) {
    try {
      const u = new URL(url, origin);
      if (u.origin !== origin) return null;

      // Hard skip customer accounts subdomain (can cause 302/403 on prefetch)
      if (u.hostname && u.hostname.startsWith('account.')) return null;

      const p = u.pathname.toLowerCase();

      // Shopify customer accounts / auth endpoints can 302/403 when prefetched; skip them.
      if (p.startsWith('/authentication')) return null;
      if (p.startsWith('/customer_authentication')) return null;
      if (p.startsWith('/customer_authentication/redirect')) return null;
      if (p.includes('oauth/authorize')) return null;

      if (SKIP_EXT_RE.test(p)) return null;
      if (SKIP_PATH_PREFIXES.some((pre) => p.startsWith(pre))) return null;

      // Strip common tracking params
      STRIP_PARAMS.forEach((k) => u.searchParams.delete(k));

      // Don’t prefetch same-page hash jumps
      u.hash = '';

      return u.toString();
    } catch {
      return null;
    }
  }

  function addPrefetch(url) {
    if (!url || seen.has(url)) return;

    // Re-validate (belt & braces) in case something slips past normalise()
    try {
      const u = new URL(url, origin);
      if (u.origin !== origin) return;
      if (u.hostname && u.hostname.startsWith('account.')) return;

      const p = (u.pathname || '').toLowerCase();
      if (p.startsWith('/authentication')) return;
      if (p.startsWith('/customer_authentication')) return;
      if (p.startsWith('/customer_authentication/redirect')) return;
      if (p.includes('oauth/authorize')) return;
    } catch {
      return;
    }

    seen.add(url);

    const link = document.createElement('link');
    link.rel = 'prefetch';
    link.as = 'document';
    link.href = url;
    link.setAttribute('data-cbh-prefetch', '1');
    document.head.appendChild(link);
  }

  function schedule(url) {
    if (!url || seen.has(url)) return;

    // Small delay avoids prefetching while cursor passes over many links
    clearTimeout(hoverTimer);
    hoverTimer = window.setTimeout(() => {
      if ('requestIdleCallback' in window) {
        requestIdleCallback(() => addPrefetch(url), { timeout: 1500 });
      } else {
        addPrefetch(url);
      }
    }, 65);
  }

  function getAnchor(target) {
    return target && target.closest ? target.closest('a[href]') : null;
  }

  function shouldSkipAnchor(a) {
    if (!a) return true;
    if (a.hasAttribute('download')) return true;
    if (a.getAttribute('target') && a.getAttribute('target') !== '_self') return true;
    if (a.hasAttribute('data-no-prefetch')) return true;
    return false;
  }

  function handleIntent(e) {
    const a = getAnchor(e.target);
    if (shouldSkipAnchor(a)) return;

    const url = normalise(a.href);
    if (!url) return;

    schedule(url);
  }

  document.addEventListener('pointerover', handleIntent, { passive: true, capture: true });
  document.addEventListener('focusin', handleIntent, { passive: true, capture: true });

  // Touch devices: prefetch the first “intended” link on touchstart
  document.addEventListener('touchstart', (e) => {
    const a = getAnchor(e.target);
    if (shouldSkipAnchor(a)) return;

    const url = normalise(a.href);
    if (!url) return;

    schedule(url);
  }, { passive: true, capture: true });
})();